/**
 * 评分表「建议分值合计 = 100」的公共校验逻辑。
 *
 * 原先内联在 CreateTableModal 里，现在「按模板上传评分表」解析 Excel 后也要用
 * 同一套校验（通过才允许调用 POST /table），因此抽到这里共用，避免两处各写一份、
 * 校验口径不一致。
 *
 * 校验口径（与创建评分表弹窗完全一致）：
 * - 基本因素层：全部 basic 的建议分值合计 = 100
 * - 派生因素层：按 parent_id 分组，每组合计 = 100
 * - 因子层：按 parent_id 分组，每组合计 = 100
 * - status = 0（勾了「不用填（100分）」）的因子不计入
 * - 没有任何子项的分组视为不参与校验（避免空组把结果判成 false）
 *
 * 建议分值为非必填：
 * - 未填的建议分值（null / undefined）不参与合计计算
 * - 如果一组中所有项都未填建议分值，跳过该校验
 * - 如果一组中有填建议分值的项，填了的那些合计必须等于 100
 */

import { List } from "immutable";

/**
 * 该因子是否计入合计校验。
 * status = 0 表示勾了「不用填（100分）」，跳过校验。
 */
export const isFillable = (factor) => factor.get("status", 1) !== 0;

/**
 * 建议分值是否「填了」（非 null / undefined）。
 * 区分「没填（空）」和「填了 0」。
 */
export const hasSuggestScore = (factor) => {
  const v = factor.get("suggest_score");
  return v != null;
};

/**
 * 取建议分值，空值（null / undefined）返回 null。
 */
export const readSuggestScore = (factor) => {
  const v = factor.get("suggest_score");
  if (v == null) return null;
  const num = Number(v);
  return Number.isNaN(num) ? null : num;
};

/**
 * 一组因子的建议分值合计（仅计入 status ≠ 0 且建议分值非空的项）。
 * 对应各 Step 底部「建议分值合计」Tag 的取值。
 */
export const sumFillable = (list) =>
  (list || new List())
    .filter((f) => isFillable(f) && hasSuggestScore(f))
    .reduce((acc, factor) => acc + readSuggestScore(factor), 0);

/**
 * 按 parent_id 分组求合计，返回 { [parentId]: 合计 }。
 * 只统计 status ≠ 0 且建议分值非空的项；
 * 某父级下没有任何有效子项时，该父级不会出现在结果里（视为整组跳过校验）。
 */
export const sumByParent = (list) => {
  const totals = {};
  (list || new List())
    .filter((f) => isFillable(f) && hasSuggestScore(f))
    .forEach((item) => {
      const parentId = item.get("parent_id");
      totals[parentId] = (totals[parentId] || 0) + readSuggestScore(item);
    });
  return totals;
};

/**
 * 派生/因子层校验：每个有填写建议分值的父级分组，合计都必须 = 100。
 * 如果没有任何分组填了建议分值（全部为空），返回 true（跳过校验）。
 */
export const isGroupSumValid = (list) => {
  const totals = Object.values(sumByParent(list));
  // 没有任何填了建议分值的分组 → 跳过校验
  if (totals.length === 0) return true;
  return totals.every((total) => total === 100);
};

/**
 * 三层建议分值合计校验总入口。
 *
 * @param {Map} newTable store 中的 newTable（immutable Map）
 * @returns {{basicSum:number, basicOk:boolean, normalOk:boolean, subOk:boolean}}
 */
export const checkSuggestSum = (newTable) => {
  const basics = newTable.get("basic", new List());
  const normals = newTable.get("normal", new List());
  const subs = newTable.get("sub", new List());

  // 只统计有填建议分值的 basic
  const filledBasics = basics.filter(
    (f) => isFillable(f) && hasSuggestScore(f)
  );
  const basicSum = sumFillable(basics);

  return {
    basicSum,
    // 如果没有任何 basic 填了建议分值，跳过校验；否则合计必须 = 100
    basicOk: filledBasics.size === 0 || basicSum === 100,
    normalOk: isGroupSumValid(normals),
    subOk: isGroupSumValid(subs)
  };
};
